import { ReactiveFormHostState } from './reactive-form-host-state';

export interface ReactiveFormHostStableChange<TModel> {
    readonly value: TModel;
    readonly changedField?: string | null | undefined;
    readonly changedFields?: readonly string[] | null | undefined;
    readonly origin?: string | null | undefined;
}

export interface ReactiveFormHostStableChangesOptions<TModel> {
    readonly state: ReactiveFormHostState<TModel>;
    readonly change: ReactiveFormHostStableChange<TModel> | TModel | null | undefined;
}

const previousDataChangeTypes = new Set(['previousDataPatched', 'previousDataChanged']);

/** Normalizes stable value changes emitted by the builder before hosts persist or inspect them. */
export class ReactiveFormHostStableChangesController {
    public static capture<TModel>(options: ReactiveFormHostStableChangesOptions<TModel>): TModel {
        const value = this.extractValue(options.change);

        options.state.setModelChanges(value);
        options.state.markStableValueChanged();
        return value;
    }

    public static isPreviousDataEvent(event: { readonly type?: string | null | undefined } | null | undefined): boolean {
        return previousDataChangeTypes.has(event?.type ?? '');
    }

    private static extractValue<TModel>(change: ReactiveFormHostStableChange<TModel> | TModel | null | undefined): TModel {
        if (this.isStableChange(change)) {
            return change.value;
        }

        return change ?? ({} as TModel);
    }

    private static isStableChange<TModel>(
        change: ReactiveFormHostStableChange<TModel> | TModel | null | undefined,
    ): change is ReactiveFormHostStableChange<TModel> {
        return typeof change === 'object' && change !== null && 'value' in change;
    }
}
